import { Cart } from '@diamond/shared/types';
import { calculateFinalPrice, trackEvent } from '@diamond/shared/utils';
import { TRACKING_EVENTS, TRACKING_PAGES } from '@diamond/sol-buyer/constant';
import { useAuthStore, useCartStore } from '@diamond/sol-buyer-context';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useCartActions } from '../useCartActions';

export interface UseCartDeleteItemArgs {
  itemId: string;
  item: Cart;
  notify?: (type: 'success' | 'error', message: string) => void;
}

export interface UseCartDeleteItemReturn {
  handleDeleteItem: () => void;
  isDeleting: boolean;
}

export const useCartDeleteItem = ({
  itemId,
  item,
  notify,
}: UseCartDeleteItemArgs): UseCartDeleteItemReturn => {
  const token = useAuthStore((state) => state.access_token);
  const { deleteItem } = useCartActions({ token, notify });
  const removeFromCart = useCartStore((state) => state.remove);

  const mutation = useMutation({
    mutationFn: async () => {
      await deleteItem(itemId);
    },
    onSuccess: () => {
      removeFromCart(itemId);

      const finalPrice = calculateFinalPrice(
        item.product,
        item.quantity / 1000
      );
      const itemValue = finalPrice * (item.quantity / 1000);

      const discountPromo = item.product.discount_promos?.[0];
      const bundledDiscountPromo = item.product.bundled_discount_promos?.[0];
      const freeGoods = item.product.free_good_promos?.[0];
      const bundledFreeGoods = item.product.bundled_free_good_promos?.[0];
      const promoForTracking =
        bundledDiscountPromo ||
        discountPromo ||
        bundledFreeGoods ||
        freeGoods ||
        null;

      const regularPrice = item.product.prices?.[0]?.price || 0;
      const discountAmount =
        regularPrice > finalPrice ? regularPrice - finalPrice : 0;

      trackEvent(TRACKING_EVENTS.REMOVE_FROM_CART, {
        currency: 'IDR',
        value: itemValue,
        item_id: item.product.item_code?.toString(),
        item_name: item.product.title,
        item_brand: item.product.brand_name,
        item_category: item.product.category_name,
        item_category2: item.product.sub_category_name,
        price: finalPrice,
        quantity: item.quantity / 1000,
        discount: discountAmount > 0 ? discountAmount : null,
        promotion_id: promoForTracking
          ? `${promoForTracking.mechanism} | ${item.product.item_code}`
          : null,
        promotion_name: promoForTracking?.mechanism || null,
        section_name: TRACKING_PAGES.CART,
      });
    },
  });

  const handleDeleteItem = useCallback(() => {
    mutation.mutate();
  }, [mutation]);

  return {
    handleDeleteItem,
    isDeleting: mutation.isPending,
  };
};
